/**
 * Enhanced Error Display Component for Historical Reports
 * 
 * Provides user-friendly error messages with appropriate actions
 */

import React from 'react';
import {
    HistoricalReportsError,
    HistoricalReportsErrorCode,
    ERROR_MESSAGES
} from '../../utils/historicalReportsErrors';

interface HistoricalReportsErrorDisplayProps {
    error: HistoricalReportsError | string | null;
    onRetry?: () => void;
    onDismiss?: () => void;
    className?: string;
    size?: 'sm' | 'md' | 'lg';
}

export const HistoricalReportsErrorDisplay: React.FC<HistoricalReportsErrorDisplayProps> = ({
    error,
    onRetry,
    onDismiss,
    className = '',
    size = 'md'
}) => {
    if (!error) return null;

    // Handle string errors
    if (typeof error === 'string') {
        return (
            <div className={`bg-red-500/10 border border-red-500/20 rounded-lg p-4 ${className}`}>
                <div className="flex items-start gap-3">
                    <div className="text-red-400 flex-shrink-0">
                        <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v3.75m-9 3.75a9 9 0 0118 0z" />
                        </svg>
                    </div>
                    <div className="flex-1">
                        <p className="text-red-400 text-sm">{error}</p>
                    </div>
                    {onDismiss && (
                        <button
                            onClick={onDismiss}
                            className="text-red-400 hover:text-red-300 flex-shrink-0"
                            aria-label="Dismiss error"
                        >
                            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    )}
                </div>
                {onRetry && (
                    <div className="mt-3">
                        <button
                            onClick={onRetry}
                            className="px-3 py-1 bg-red-600 hover:bg-red-700 rounded text-white text-sm transition-colors"
                        >
                            Try Again
                        </button>
                    </div>
                )}
            </div>
        );
    }

    // Handle structured errors
    const errorConfig = ERROR_MESSAGES[error.code] || ERROR_MESSAGES[HistoricalReportsErrorCode.UNKNOWN_ERROR];

    const sizeClasses = {
        sm: 'p-3',
        md: 'p-4',
        lg: 'p-6'
    };

    const iconSizes = {
        sm: 'h-4 w-4',
        md: 'h-5 w-5',
        lg: 'h-6 w-6'
    };

    return (
        <div className={`bg-red-500/10 border border-red-500/20 rounded-lg ${sizeClasses[size]} ${className}`}>
            <div className="flex items-start gap-3">
                <div className="text-red-400 flex-shrink-0">
                    <ErrorIcon code={error.code} className={iconSizes[size]} />
                </div>

                <div className="flex-1 min-w-0">
                    <h3 className="text-red-400 font-medium text-sm mb-1">
                        {errorConfig.title}
                    </h3>
                    <p className="text-red-300 text-sm leading-relaxed">
                        {error.userMessage || errorConfig.message}
                    </p>

                    {/* Development details */}
                    {process.env.NODE_ENV === 'development' && error.details && (
                        <details className="mt-2">
                            <summary className="text-red-400 text-xs cursor-pointer hover:text-red-300">
                                Technical Details
                            </summary>
                            <pre className="mt-1 text-xs text-red-200 bg-red-950/20 rounded p-2 overflow-x-auto">
                                {JSON.stringify(error.details, null, 2)}
                            </pre>
                        </details>
                    )}
                </div>

                {onDismiss && (
                    <button
                        onClick={onDismiss}
                        className="text-red-400 hover:text-red-300 flex-shrink-0 transition-colors"
                        aria-label="Dismiss error"
                    >
                        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                )}
            </div>

            {/* Action buttons */}
            <div className="flex gap-2 mt-3">
                {onRetry && error.retryable && (
                    <button
                        onClick={onRetry}
                        className="px-3 py-1.5 bg-red-600 hover:bg-red-700 rounded text-white text-sm font-medium transition-colors"
                    >
                        {errorConfig.action}
                    </button>
                )}

                {error.code === HistoricalReportsErrorCode.AUTH_REQUIRED && (
                    <button
                        onClick={() => window.location.reload()}
                        className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 rounded text-white text-sm font-medium transition-colors"
                    >
                        Log In
                    </button>
                )}
            </div>
        </div>
    );
};

/**
 * Error icon component based on error type
 */
const ErrorIcon: React.FC<{ code: HistoricalReportsErrorCode; className?: string }> = ({ code, className = "h-5 w-5" }) => {
    switch (code) {
        case HistoricalReportsErrorCode.NETWORK_ERROR:
        case HistoricalReportsErrorCode.API_TIMEOUT:
            return (
                <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
            );

        case HistoricalReportsErrorCode.AUTH_REQUIRED:
        case HistoricalReportsErrorCode.AUTH_INVALID:
        case HistoricalReportsErrorCode.AUTH_EXPIRED:
            return (
                <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m0 0v2m0-2h2m-2 0h-2m8-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
            );

        case HistoricalReportsErrorCode.API_NOT_FOUND:
        case HistoricalReportsErrorCode.REPORT_NOT_FOUND:
            return (
                <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 12h6m-6 4h6m6 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
            );

        case HistoricalReportsErrorCode.API_SERVER_ERROR:
            return (
                <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
            );

        case HistoricalReportsErrorCode.RATE_LIMITED:
            return (
                <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
            );

        default:
            return (
                <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
                </svg>
            );
    }
};

/**
 * Compact error display for smaller spaces
 */
export const CompactErrorDisplay: React.FC<HistoricalReportsErrorDisplayProps> = (props) => (
    <HistoricalReportsErrorDisplay {...props} size="sm" />
);

/**
 * Full-page error display for critical errors
 */
export const FullPageErrorDisplay: React.FC<HistoricalReportsErrorDisplayProps> = (props) => (
    <div className="min-h-[400px] flex items-center justify-center p-4">
        <div className="max-w-md w-full">
            <HistoricalReportsErrorDisplay {...props} size="lg" />
        </div>
    </div>
);